/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCart } from '@shared/cart-react';
import type { ImageType, RatingsType } from '@shared/react-ui-core';
import {
  AsSeenOn,
  CallChat,
  CoveEquipment,
  CustomerRatings,
  CustomersLoveCove,
  Divider,
  FAQ,
  FasterResponse,
  HomepageHero,
  Reels,
  Showcase,
  SimpleSteps,
  USPIcons,
  useWindowSize,
} from '@shared/react-ui-core';
import AffirmPageBanner from '@website/components/affirm/AffirmPageBanner/AffirmPageBanner';
import EufyPartners from '@website/components/eufyPartners';
import Comparison from '@website/components/homeNew/experienceCove';
import Layout from '@website/components/Layout/layout';
import Seo, { type SeoFragmentPage } from '@website/components/seo';
import { getCookie, setCookie } from '@website/libs/utils';
import { useMixpanel } from 'gatsby-plugin-mixpanel';
import { graphql } from 'gatsby';
import React, { useEffect, useState } from 'react';
import useEmotive from '@website/hooks/useEmotive';
import QuickLinks from '@website/components/quickLinks/quickLinks';

function useLocalStorage(key: string) {
  const [value, setValue] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(key);
    }
    return null;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const interval = setInterval(() => {
        const newValue = localStorage.getItem(key);
        if (newValue !== value) {
          setValue(newValue);
        }
      }, 5000); // Check every 5 seconds

      return () => {
        clearInterval(interval);
      };
    }
  }, [key, value]);

  return value;
}

const defaultImages: ImageType[] = [
  {
    name: 'hero-section-new',
    id: 'hero-image',
    image: {
      url: 'https://www.datocms-assets.com/10154/1696969721-call-230622-cove-0052_screen-1.png',
      alt: 'Cove equipment on table',
    },
  },
  {
    name: 'showcase-section',
    id: 'showcase-image',
    image: {
      url: 'https://www.datocms-assets.com/10154/1725890764-alarm-panel-and-vase-on-table.png',
      alt: 'alarm panel and vase on table',
    },
  },
  {
    name: 'hero-section',
    id: 'showcase-image',
    image: {
      url: 'https://www.datocms-assets.com/10154/1687191456-alarm-display-on-side-table.png',
      alt: 'alarm display on side table',
    },
  },
];

const Index: React.FC<{
  location: { pathname: String };
  data: SeoFragmentPage & { datoCmsSiteMetaInfo: RatingsType } & {
    datoCmsComponent: { images: ImageType[] };
  };
}> = ({ data, location }) => {
  const { edgetag } = useEmotive();
  const mixpanel = useMixpanel();
  const { datoCmsSeo, datoCmsComponent } = data;
  const {
    images,
    reviews,
    heroImage,
    heroSubtext,
    heroText,
    hideBanner,
    navLogo,
  } = datoCmsSeo;
  const ratings = data.datoCmsSiteMetaInfo;
  const getImageByName = (name: string) => {
    const img = images?.filter((image) => image.name === name)?.[0]?.image;

    if (!img) {
      return defaultImages.filter((image) => image.name === name)?.[0]?.image;
    }

    return img;
  };
  const { width = 1920 } = useWindowSize();
  const cart = useCart();
  const efTransactionId = useLocalStorage('ef_transaction_id');

  const showCaseItems = [
    {
      text: 'Easy to set up and use (professional installation available)',
      imgURL:
        'https://www.datocms-assets.com/10154/1716310525-home_checkmark_icon.svg',
    },
    {
      text: 'Ethernet connection with cellular backup so you’re always connected',
      imgURL:
        'https://www.datocms-assets.com/10154/1716310966-lock_checkmark.svg',
    },
    {
      text: 'Faster emergency dispatch and fewer false alarms',
      imgURL:
        'https://www.datocms-assets.com/10154/1716311359-shield_checkmark_icon.svg',
    },
  ];

  useEffect(() => {
    const landingCookie = getCookie('landing');
    if (!landingCookie) {
      // eslint-disable-next-line no-restricted-globals
      setCookie('landing', location?.pathname, 90);
    }
    edgetag.trackViewContent({});
    mixpanel.register_once({ landing_page: location?.pathname });
  }, []);

  useEffect(() => {
    if (efTransactionId && cart.efTransactionId === null) {
      cart?.addEverflow?.({ id: efTransactionId });
    }
  }, [efTransactionId, cart]);

  return (
    <Layout hidePromo={!!hideBanner}>
      <QuickLinks />
      <div>
        <span id="popout-quiz-trigger" style={{ width: '100%' }} />
        <div>
          <HomepageHero
            image={heroImage || getImageByName('hero-section-new')}
            {...{
              title: heroText?.length ? heroText : undefined,
              subtitle: heroSubtext?.length ? heroSubtext : undefined,
            }}
          />
          <USPIcons />
          <AsSeenOn />
          <AffirmPageBanner />
          <Showcase
            image={getImageByName('showcase-section')}
            title="Home security that’s both effective and affordable."
            subtitle=""
            altItems={showCaseItems}
          />
          <CustomerRatings ratings={ratings} />
          <Divider
            style={{ maxWidth: 1440, margin: '0 auto', width: '100%' }}
          />
          <EufyPartners />
          <CoveEquipment images={datoCmsComponent?.images} />
          <SimpleSteps />
          <Reels />
          <FasterResponse />
          <Comparison
            title="It's an easy decision."
            css={css`
              & h2 {
                margin-bottom: 35px;
              }
            `}
          />
          <CustomersLoveCove reviews={reviews} />
        </div>
        <CallChat
          css={css`
            margin: 50px auto;
          `}
        />
        <FrequentlyAskedQuestions />
      </div>
    </Layout>
  );
};

const FrequentlyAskedQuestions = () => {
  return (
    <FAQ
      questions={[
        {
          heading: `Do I need Wi-Fi?`,
          details: `
            <p>No. Our hub is set up so you can connect it directly to your internet router, and it comes with 4G cellular backup, so if you don't have internet, no problem. This dual ethernet/4G connection means that if your internet goes down, your security system will keep working.</p>
            <p>Our sensors connect to the hub via a radio connection. Our cameras do require a 2.4 GHz Wi-Fi connection, but they are the only equipment items that rely on Wi-Fi.</p>
            `,
        },
        {
          heading: `Do I need a certain cell phone provider?`,
          details: `No. When you sign up for a Cove monitoring plan, we handle the 4G cellular connection and subscription ourselves. It is completely separate from your personal cell phone plan. If you're concerned about the coverage in your area, you can always give us a try with our 60-day money-back guarantee.`,
        },
        {
          heading: `What happens if the power goes out?`,
          details: `If your power goes out, you might be alone in a dark house with no Netflix, but Cove will continue to protect your home. The hub includes a 24-hour backup battery to protect your home during power outages.`,
        },
        {
          heading: `What equipment does Cove offer?`,
          details: `Cove offers a variety of professional equipment, including a hub, a touchscreen alarm panel, indoor and outdoor cameras, anti-burglary sensors, environmental sensors, medical alerts, and keyfobs.`,
        },
        {
          heading: `How do I install Cove's security system?`,
          details: `
          <p>To initiate the installation process, begin by connecting your hub and touchscreen panel to a power source. Once powered, wait for the indicator light signaling network connectivity before proceeding to connect the touchscreen panel. Our on-screen installation guide will then seamlessly guide you through the process of naming and strategically positioning each sensor within your home.</p>
          <p>For added convenience, adhesive strips are provided with each device to facilitate effortless sensor placement. The duration of setup is contingent upon the quantity of sensors being installed. Should you encounter any challenges during setup, our dedicated customer support team stands ready to provide assistance.</p>
          `,
        },
        {
          heading: `Where should I install the primary touchscreen panel?`,
          details: `
          The touchscreen serves as the primary interface through which users engage with the system. Given its pivotal role, we recommend installing it in a location that is quick to access when entering and exiting the premises. Should you require multiple accessible touchscreens, the hub will support up to four.
          `,
        },
      ]}
    />
  );
};

export const Head = ({ data }: { data: SeoFragmentPage }) => (
  <Seo seo={data?.datoCmsSeo} />
);

export default Index;

export const query = graphql`
  query ($slug: String, $language: String) {
    ...SeoFragmentPage

    datoCmsSeo(path: { eq: $slug }, locale: $language) {
      heroText
      heroSubtext
      heroImage {
        alt
        url
      }
      navLogo {
        alt
        url
      }
      hideBanner
      reviews {
        profileImage {
          url
          alt
        }
        reviewContent
        reviewDate
        reviewerName
        customData
        id
      }
    }

    datoCmsComponent(name: { eq: "Cove Equipment" }) {
      images {
        id
        name
        image {
          alt
          url
        }
      }
    }
  }
`;
